import { useState } from 'react'
import { go, useArrived, useTooltip, useWidth } from './Tooltip'

// Findings raised and closed per bucket: two thin columns per bucket on one
// axis, the raised series first, a 2px surface gap between the pair, 4px round
// tops and square feet, and three hairline gridlines. A crosshair finds the
// bucket under the pointer and the tooltip names both series and how many
// stayed open.
//
// Any number of buckets: twelve weeks, seven or thirty days, thirteen weeks.
// The columns thin with the space, and the labels thin out to every second,
// third or fifth bucket so they never touch, counted back from the last one so
// the newest bucket is always named. The first and last labels are kept inside
// the plot's width, which matters at thirty buckets on a phone.
//
// The plot is drawn at its measured width, with the proposal's margins (28px
// left for the axis, 24px under it for the labels), so a wide card spaces the
// buckets out rather than stretching the marks.
//
// Arrow keys move through the buckets once the chart has focus, and the tooltip
// follows them, so the readout is not a pointer-only feature.

export type Week = { label: string; a: number; b: number; open: number }

const H = 210
const L = 28
const R = 8
const T = 10
const B = 24
// The widest label ("30 Sep" at 11px) and the least air between two of them.
const LABEL_W = 38
const LABEL_GAP = 8

function niceMax(v: number): number {
  if (v <= 4) return 4
  const step = Math.pow(10, Math.floor(Math.log10(v)))
  for (const m of [1, 2, 2.5, 5, 10]) {
    if (m * step >= v) return m * step
  }
  return 10 * step
}

export function WeeklyColumns({
  weeks,
  names,
  colors,
  openLabel,
  href,
  title,
  unit = 'week',
}: {
  weeks: Week[]
  names: [string, string]
  colors: [string, string]
  openLabel: string
  href?: string
  title: string
  // How the tooltip names a bucket: "Week of 08 Sep", or the day itself.
  unit?: 'week' | 'day'
}) {
  const arrived = useArrived()
  const tip = useTooltip()
  const [ref, W] = useWidth<HTMLDivElement>(560)
  const [at, setAt] = useState<number | null>(null)

  const n = Math.max(weeks.length, 1)
  const pw = Math.max(W - L - R, 10)
  const ph = H - T - B
  const top = niceMax(Math.max(0, ...weeks.map((w) => Math.max(w.a, w.b))))
  const y = (v: number) => T + ph - (v / top) * ph
  const band = pw / n
  // The air between buckets is a quarter of the bucket and never under 2px,
  // and the pair shares what is left with a 2px gap between them.
  const bw = Math.max(1.5, Math.min(12, (band - Math.max(2, band * 0.25) - 2) / 2))
  const cx = (i: number) => L + band * i + band / 2
  const every = Math.max(1, Math.ceil((LABEL_W + LABEL_GAP) / band))
  const labelAt = (i: number) => {
    const x = cx(i)
    if (x + LABEL_W / 2 > W) return { x: W, anchor: 'end' as const }
    // Clear of the axis numbers on the left, which end just short of the plot.
    if (x - LABEL_W / 2 < L) return { x: Math.max(L, x - band / 2), anchor: 'start' as const }
    return { x, anchor: 'middle' as const }
  }
  const delay = Math.min(35, 420 / n)

  const rowsAt = (i: number) => {
    const w = weeks[i]
    if (!w) return []
    return [
      { label: names[0], value: String(w.a), color: colors[0] },
      { label: names[1], value: String(w.b), color: colors[1] },
      { label: openLabel, value: String(w.open), color: 'var(--ink)' },
    ]
  }
  const titleAt = (i: number) => (unit === 'week' ? 'Week of ' : '') + (weeks[i]?.label ?? '')

  const col = (v: number, x: number) => {
    const h = ph * (v / top)
    if (h <= 0) return ''
    const rr = Math.min(4, h / 2, bw / 2)
    const t0 = T + ph - h
    const x2 = x + bw
    return `M${x} ${T + ph}V${t0 + rr}Q${x} ${t0} ${x + rr} ${t0}H${x2 - rr}Q${x2} ${t0} ${x2} ${t0 + rr}V${T + ph}Z`
  }

  return (
    <div
      ref={ref}
      className={'hc-chart' + (arrived ? ' on' : '')}
      tabIndex={0}
      role="img"
      aria-label={title + '. ' + weeks.map((w) => w.label + ': ' + w.a + ' ' + names[0].toLowerCase() + ', ' + w.b + ' ' + names[1].toLowerCase()).join('; ')}
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault()
          const i = at == null ? n - 1 : Math.max(0, Math.min(n - 1, at + (e.key === 'ArrowRight' ? 1 : -1)))
          setAt(i)
          const mark = e.currentTarget.querySelector('[data-week="' + i + '"]')
          if (mark) tip.showAt(mark, titleAt(i), rowsAt(i))
        } else if (e.key === 'Enter') {
          go(href)
        } else if (e.key === 'Escape') {
          setAt(null)
          tip.hide()
        }
      }}
      onFocus={(e) => {
        const i = at ?? n - 1
        setAt(i)
        const mark = e.currentTarget.querySelector('[data-week="' + i + '"]')
        if (mark) tip.showAt(mark, titleAt(i), rowsAt(i))
      }}
      onBlur={() => {
        setAt(null)
        tip.hide()
      }}
    >
      <svg viewBox={'0 0 ' + W + ' ' + H} aria-hidden="true">
        {[0, top / 2, top].map((v) => (
          <g key={v}>
            <line className="hc-gridl" x1={L} x2={W - R} y1={y(v)} y2={y(v)} />
            <text className="hc-axis" x={L - 6} y={y(v) + 4} textAnchor="end">
              {Number.isInteger(v) ? v : v.toFixed(1)}
            </text>
          </g>
        ))}
        {weeks.map((w, i) => (
          <g key={i}>
            <path className="hc-bar-a hc-grow" d={col(w.a, cx(i) - bw - 1)} style={{ transitionDelay: i * delay + 'ms', fill: colors[0] }} />
            <path className="hc-bar-b hc-grow" d={col(w.b, cx(i) + 1)} style={{ transitionDelay: i * delay + 'ms', fill: colors[1] }} />
            <rect data-week={i} x={cx(i) - 1} y={T} width={2} height={1} fill="transparent" />
            {(n - 1 - i) % every === 0 && (
              <text className="hc-axis" x={labelAt(i).x} y={H - 6} textAnchor={labelAt(i).anchor}>
                {w.label}
              </text>
            )}
          </g>
        ))}
        {at != null && <line className="hc-hover-x" x1={cx(at)} x2={cx(at)} y1={T} y2={T + ph} />}
        <rect
          x={L}
          y={T}
          width={pw}
          height={ph}
          fill="transparent"
          style={{ cursor: href ? 'pointer' : 'default' }}
          onPointerMove={(e) => {
            const r = (e.currentTarget.ownerSVGElement as SVGSVGElement).getBoundingClientRect()
            const sx = ((e.clientX - r.left) / r.width) * W
            const i = Math.max(0, Math.min(n - 1, Math.floor((sx - L) / band)))
            setAt(i)
            tip.show(e, titleAt(i), rowsAt(i))
          }}
          onPointerLeave={() => {
            setAt(null)
            tip.hide()
          }}
          onClick={() => go(href)}
        />
      </svg>
    </div>
  )
}
